import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import logisticsApi from '@utils/logisticsApi';
import { useI18n } from '@/i18n';
import PageHeader from '@components/PageHeader';
import { IconShirt } from '@components/icons';

function AdjustmentForm({ issueId, onDone, onCancel }) {
  const { t } = useI18n();
  const [deltaMonths, setDeltaMonths] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault();
    if (!deltaMonths || !reason.trim()) { setError(t('logisticsUniform.errFill')); return; }
    setBusy(true);
    setError('');
    try {
      await logisticsApi.post('/me/renewal-adjustments', { issueId, deltaMonths: Number(deltaMonths), reason });
      onDone();
    } catch (err) {
      setError(err.response?.data?.message || t('logisticsUniform.errSend'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="mt-3 pt-3 border-t border-gray-100 space-y-2">
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <input type="number" className="input w-20" placeholder={t('logisticsUniform.deltaMonths')}
          value={deltaMonths} onChange={(e) => setDeltaMonths(e.target.value)} />
        <input className="input flex-1" placeholder={t('logisticsUniform.reasonPlaceholder')}
          value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="btn-ghost flex-1 text-xs">{t('logisticsUniform.cancel')}</button>
        <button type="submit" disabled={busy} className="btn-primary flex-1 text-xs">
          {busy ? t('logisticsUniform.sending') : t('logisticsUniform.send')}
        </button>
      </div>
    </form>
  );
}

export default function MyUniform() {
  const { t, lang } = useI18n();
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busyId,  setBusyId]  = useState(null);
  const [formFor, setFormFor] = useState(null);

  const load = useCallback(() => {
    setLoadError(false);
    logisticsApi.get('/me/uniform')
      .then(({ data }) => setItems(data.uniform || []))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function confirmReceipt(issueId) {
    setBusyId(issueId);
    try {
      await logisticsApi.post(`/me/uniform/${issueId}/confirm-receipt`);
      load();
    } catch { /* ignore, item just stays unconfirmed */ }
    finally { setBusyId(null); }
  }

  return (
    <>
      <PageHeader title={t('logisticsUniform.title')} subtitle={t('logisticsUniform.subtitle')} />
      <div className="px-5 md:px-0 pb-6">
        {loading ? (
          <div className="card p-8 text-center text-gray-400">{t('common.loading')}</div>
        ) : loadError ? (
          <div className="card p-10 text-center">
            <p className="text-sm text-red-600 mb-3">{t('logisticsUniform.errLoad')}</p>
            <button onClick={load} className="btn-ghost text-xs">{t('logisticsUniform.retry')}</button>
          </div>
        ) : items.length === 0 ? (
          <div className="card p-10 text-center">
            <div className="mx-auto w-12 h-12 grid place-items-center rounded-xl bg-gray-100 text-gray-400 mb-3">
              <IconShirt size={24} />
            </div>
            <p className="text-sm text-gray-500">{t('logisticsUniform.empty')}</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {items.map((u, i) => (
              <motion.div key={u.id ?? i}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                className="card p-4">
                <div className="flex items-center gap-4">
                  <span className="shrink-0 grid place-items-center w-10 h-10 rounded-xl bg-glk-blue/10 text-glk-blue">
                    <IconShirt size={20} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-glk-dark truncate">{u.item_name}</p>
                    <p className="text-xs text-gray-400 truncate">
                      {t(`logisticsUniform.status.${u.status}`) || u.status}
                      {u.renewal_due_date && (
                        <> · {new Date(u.renewal_due_date).toLocaleDateString(lang === 'en' ? 'en-GB' : 'pt-PT')}</>
                      )}
                    </p>
                  </div>
                </div>

                {u.status === 'active' && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2">
                    {!u.confirmed_by_staff_at && (
                      <button disabled={busyId === u.id} onClick={() => confirmReceipt(u.id)}
                        className="btn-primary flex-1 text-xs">
                        {busyId === u.id ? t('logisticsUniform.confirming') : t('logisticsUniform.confirmReceipt')}
                      </button>
                    )}
                    <button onClick={() => setFormFor(formFor === u.id ? null : u.id)}
                      className="btn-ghost flex-1 text-xs">
                      {t('logisticsUniform.requestAdjustment')}
                    </button>
                  </div>
                )}

                {formFor === u.id && (
                  <AdjustmentForm issueId={u.id} onCancel={() => setFormFor(null)} onDone={() => { setFormFor(null); load(); }} />
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
